import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Award, CheckCircle2, Clock, ExternalLink } from 'lucide-react';
import axios from 'axios';
import { certifications as fallbackCerts } from '../utils/mockData';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Certifications = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [certifications, setCertifications] = useState(fallbackCerts);

  useEffect(() => {
    axios
      .get(`${API}/content/certifications`)
      .then((res) => {
        if (Array.isArray(res.data) && res.data.length) setCertifications(res.data);
      })
      .catch(() => {});
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.08 } },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: [0.645, 0.045, 0.355, 1] },
    },
  };

  return (
    <section className="certs-section" id="certifications">
      <div className="section-container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6 }}
          className="section-header"
        >
          <h2 className="section-title">Certifications</h2>
          <p className="section-subtitle">
            Verified credentials across OpenShift, Terraform, Azure, Oracle Cloud and Red Hat automation.
          </p>
        </motion.div>

        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          className="certs-grid"
        >
          {certifications.map((cert) => {
            const inProgress = cert.status === 'inprogress' || cert.status === 'in-progress' || cert.status === 'In Progress';
            const inactive = cert.status === 'inactive' || cert.status === 'Inactive';
            const hasCredly = cert.credlyUrl && cert.credlyUrl.trim().length > 0;
            const CardTag = hasCredly ? motion.a : motion.div;
            const linkProps = hasCredly
              ? { href: cert.credlyUrl, target: '_blank', rel: 'noopener noreferrer' }
              : {};
            return (
              <CardTag
                key={cert.id}
                {...linkProps}
                variants={cardVariants}
                className={`cert-card ${hasCredly ? 'cert-linked' : ''}`}
                whileHover={{ y: -6, transition: { duration: 0.3 } }}
                data-testid="cert-card"
              >
                <div className="cert-card-top">
                  <Award className="cert-icon" size={24} />
                  <span className={`cert-status ${inProgress ? 'progress' : inactive ? 'inactive' : 'active'}`}>
                    {inProgress ? <Clock size={13} /> : <CheckCircle2 size={13} />}
                    {cert.statusLabel || (inProgress ? 'In Progress' : inactive ? 'Inactive' : 'Active')}
                  </span>
                </div>
                <h3 className="cert-name">{cert.name}</h3>
                <div className="cert-meta">
                  <span className="cert-issuer">{cert.issuer}</span>
                  {cert.expiry && <span className="cert-expiry">Valid to {cert.expiry}</span>}
                </div>
                {hasCredly && (
                  <span className="cert-verify">
                    <ExternalLink size={13} /> Verify on Credly
                  </span>
                )}
              </CardTag>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

export default Certifications;
